import type { SearchArticleMeta } from "@serophin/nottldr-shared";
import { createContext, createRef, type RefObject } from "preact";
import { useContext } from "preact/hooks";

/*
 * The seam the public pages use to play audio. The real player is `audio/AudioPlayerProvider`, mounted in
 * SiteApp; outside it (the server render) the context holds the do-nothing player below.
 */

export type AudioLoadingState = "idle" | "loading" | "loaded" | "error";

export type CurrentArticle = { authorAlias: string; articleId: string; title?: string };

export type AudioPlayer = {
  currentArticle: CurrentArticle | null;
  isPlaying: boolean;
  loadingState: AudioLoadingState;
  currentTime: number;
  duration: number;
  /** The time under the finger or pointer while the progress bar is dragged, else null. */
  dragPreviewTime: number | null;
  playbackSpeed: number;
  progressBarRef: RefObject<HTMLDivElement>;
  isArticlePlaying: (authorAlias: string, articleId: string) => boolean;
  togglePlayPause: (authorAlias: string, articleId: string, articleTitle?: string) => Promise<void>;
  /** The list the visible articles form: when one ends, the next one starts. */
  setPlaylist: (metas: SearchArticleMeta[]) => void;
  pause: () => void;
  skipBackward: (seconds: number) => void;
  skipForward: (seconds: number) => void;
  cyclePlaybackSpeed: () => void;
  handleProgressClick: (event: MouseEvent) => void;
  handleMouseDown: (event: MouseEvent) => void;
  handleTouchStart: (event: TouchEvent) => void;
};

const nothing = (): void => {};

export const NO_AUDIO_PLAYER: AudioPlayer = {
  currentArticle: null,
  isPlaying: false,
  loadingState: "idle",
  currentTime: 0,
  duration: 0,
  dragPreviewTime: null,
  playbackSpeed: 1,
  progressBarRef: createRef<HTMLDivElement>(),
  isArticlePlaying: () => false,
  togglePlayPause: async () => {},
  setPlaylist: nothing,
  pause: nothing,
  skipBackward: nothing,
  skipForward: nothing,
  cyclePlaybackSpeed: nothing,
  handleProgressClick: nothing,
  handleMouseDown: nothing,
  handleTouchStart: nothing,
};

export const AudioPlayerContext = createContext<AudioPlayer>(NO_AUDIO_PLAYER);

export const useAudioPlayer = (): AudioPlayer => useContext(AudioPlayerContext);
